import axios, { AxiosInstance } from "axios";
import type { PhotoAiPreviewItem, PhotoAiPreviewAction } from "../types/photoAiEdit";

export const AI_PREVIEW_TIMEOUT_MS = 30_000;

type PreviewApiData = {
  preview_id: number;
  original_url?: string;
  edited_url?: string;
  url?: string;
  photo?: string;
  preview_url?: string;
  preview_url_l?: string;
  user_prompt?: string;
  improvement_summary?: string;
  from_cache?: boolean;
};

export function mapPreviewResponse(
  data: PreviewApiData,
  photoId: number,
  type: PhotoAiPreviewAction,
  fallbackOriginalUrl?: string
): PhotoAiPreviewItem {
  const edited =
    data.edited_url ??
    data.url ??
    data.preview_url ??
    data.photo ??
    data.preview_url_l ??
    "";

  return {
    id: data.preview_id,
    photo_id: photoId,
    url: edited,
    original_url: data.original_url ?? fallbackOriginalUrl ?? "",
    type,
    user_prompt: data.user_prompt,
    improvement_summary: data.improvement_summary,
    from_cache: data.from_cache,
  };
}

export async function requestCustomEditPreview(
  client: AxiosInstance,
  photoId: number,
  prompt: string,
  signal?: AbortSignal
) {
  return client.post<{ success: boolean; data: PreviewApiData; message?: string }>(
    `/api/adm/photos/${photoId}/ai-preview`,
    { action: "custom_edit", prompt },
    { signal, timeout: AI_PREVIEW_TIMEOUT_MS }
  );
}

export async function requestVirtualStagingPreview(
  client: AxiosInstance,
  photoId: number,
  signal?: AbortSignal
) {
  return client.post<{ success: boolean; data: PreviewApiData; message?: string }>(
    `/api/adm/photos/${photoId}/ai-preview`,
    { action: "virtual_staging" },
    { signal, timeout: AI_PREVIEW_TIMEOUT_MS }
  );
}

export async function applyAiPreview(
  client: AxiosInstance,
  photoId: number,
  previewId: number
) {
  return client.post(`/api/adm/photos/${photoId}/ai-apply`, {
    preview_id: previewId,
  });
}

export async function discardAiPreview(client: AxiosInstance, previewId: number) {
  return client.delete(`/api/adm/ai-previews/${previewId}`);
}

export type AiPreviewErrorInfo = {
  message: string;
  retryable: boolean;
};

export function parseAiPreviewError(err: unknown): AiPreviewErrorInfo {
  if (axios.isCancel(err)) {
    return { message: "Edição cancelada.", retryable: false };
  }

  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string; data?: { retryable?: boolean } }
      | undefined;
    const message =
      data?.message ??
      (err.code === "ECONNABORTED"
        ? "O processamento demorou mais que o esperado. Tente novamente."
        : "Não foi possível processar a imagem com IA. Tente novamente.");
    const retryable =
      data?.data?.retryable === true ||
      err.response?.status === 502 ||
      err.code === "ECONNABORTED";

    return { message, retryable };
  }

  return {
    message: "Não foi possível processar a imagem com IA. Tente novamente.",
    retryable: true,
  };
}

export function logPhotoAiPreviewGenerated(payload: {
  photoId: number;
  previewId: number;
  prompt?: string;
  improvementSummary?: string;
  fromCache?: boolean;
}) {
  console.info("[AI] Photo preview generated", payload);
}
